import React, { useEffect, useState } from 'react';
import { Table, Button, DOMHelper, Stack, IconButton, Whisper, Tooltip, toaster, Message as RsMessage, Divider, SortType, Tag, Panel, Heading } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, PagedResponseModel, PaginationFilterResult, DateTimeFormatOptions } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import { Message, MessagePaginationFilterRequest, MessageContentType, MessageContentTypeList } from '@/models/message.model';
import { deleteChannelMessage, getPagedChannelMessages } from '@/services/channel.service';
import { Icon } from '@rsuite/icons';
import { MdDelete, MdArrowBack } from 'react-icons/md';
import PageContent from '@/components/PageContent';
import ImageCell from '@/components/ImageCell';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

export interface RunningClubChannelMessagesProps {
  channelId: number;
  channelName?: string;
  onBack?: () => void;
}

const contentTypeColor = (contentType: MessageContentType) => {
  switch (contentType) {
    case MessageContentType.Image: return 'blue';
    case MessageContentType.Location: return 'violet';
    default: return 'green';
  }
};

const RunningClubChannelMessages: React.FC<RunningClubChannelMessagesProps> = ({ channelId, channelName, onBack }) => {
  const [filterReq, setFilterReq] = useState<MessagePaginationFilterRequest>(new MessagePaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq, channelId]);

  const fetchData = async (req: MessagePaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<Message> = await getPagedChannelMessages(channelId, req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Messages:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre,
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  const onDeleteItem = (id: number) => {

    const handleYesClick = async () => {
      setLoading(true);
      try {
        const result: BaseResponseModel<null> = await deleteChannelMessage(id);
        if (result.success) {
          toastSuccessMessage(`Message Removed.`);
          fetchData(filterReq);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in deleting Message:${errorModel?.errorMessage}`);
      }
      finally {
        setLoading(false);
        toaster.clear();
      }
    };

    const handleNoClick = () => {
      toaster.clear();
    };

    toaster.push(
      <RsMessage type="error" closable>
        <p style={{ marginBottom: 10 }}>Are you sure you want to remove this <strong>Message</strong>?</p>
        <div>
          <Button appearance="primary" color='red' onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={handleNoClick} style={{ marginLeft: 10 }}>No</Button>
        </div>
      </RsMessage>,
      { placement: 'topCenter', duration: 0 }
    );

  };


  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Heading>{channelName}</Heading>
        <Stack spacing={6}>
          {onBack && (
            <IconButton icon={<Icon as={MdArrowBack} />} onClick={onBack} >
              Back to Channels
            </IconButton>
          )}
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
        rowHeight={75}
      >
        <Column flexGrow={0.5} align="center" verticalAlign="middle">
          <HeaderCell>Sender Picture</HeaderCell>
          <ImageCell dataKey='sender.profilePicture'/>
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle">
          <HeaderCell>Sender</HeaderCell>
          <Cell>
            {(rowData: Message) => `${rowData.sender?.firstName} ${rowData.sender?.lastName}`}
          </Cell>
        </Column>
        <Column flexGrow={0.5} align="center" verticalAlign="middle">
          <HeaderCell>Type</HeaderCell>
          <Cell>
            {(rowData: Message) => (
              <Tag color={contentTypeColor(rowData.contentType)}>
                {MessageContentTypeList.find(t => t.value === rowData.contentType)?.label ?? rowData.contentType}
              </Tag>
            )}
          </Cell>
        </Column>
        <Column flexGrow={3} align="left" verticalAlign="middle">
          <HeaderCell>Text</HeaderCell>
          <Cell>
            {(rowData: Message) => rowData.text ?? ''}
          </Cell>
        </Column>
        <Column flexGrow={0.5} align="center" verticalAlign="middle">
          <HeaderCell>Image</HeaderCell>
          <ImageCell dataKey='link'/>
        </Column>
        <Column flexGrow={1} align="center" verticalAlign="middle">
          <HeaderCell>Location</HeaderCell>
          <Cell>
            {(rowData: Message) => (rowData.lat != null && rowData.lng != null)
            ? `[lat:${rowData.lat.toFixed(5)}, lng:${rowData.lng.toFixed(5)}]`
            : ''}
          </Cell>
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle" sortable>
          <HeaderCell>Created</HeaderCell>
          <Cell>
            {(rowData: Message) => rowData.createdAt ? rowData.createdAt.toLocaleString(undefined, DateTimeFormatOptions) : ''}
          </Cell>
        </Column>
        <Column flexGrow={0.5} align="left" verticalAlign="middle" fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: Message) => (
              <Stack spacing={16}>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Delete Message</Tooltip>}>
                  <IconButton circle size="sm" color="red" appearance="ghost"
                    onClick={() => onDeleteItem(rowData.id)}
                    icon={<Icon as={MdDelete} />} />
                </Whisper>
              </Stack>
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({ ...pre, pageNumber: dataKey }))}
        onChangeLimit={dataKey => setFilterReq(pre => ({ ...pre, pageSize: dataKey }))}
      />
    </PageContent>
  );
};

export default RunningClubChannelMessages;
